import React from "react";
import { motion } from "framer-motion";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { initialHero } from "../data/initialData";

const particles = Array.from({ length: 30 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 4 + 1,
  duration: Math.random() * 20 + 10,
  delay: Math.random() * 5,
}));

const Hero = () => {
    const [heroData] = useLocalStorage('hero', initialHero);

    return (
        <section className="relative min-h-screen flex items-center overflow-hidden bg-slate-950">
          {/* Grid background */}
          <div className="absolute inset-0 bg-grid opacity-40" />

          {/* Animated particles */}
          {particles.map((p) => (
            <motion.div
              key={p.id}
              className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
              animate={{
                x: [0, Math.random() * 100 - 50, 0],
                y: [0, Math.random() * 100 - 50, 0],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: p.duration,
                repeat: Infinity,
                delay: p.delay,
                ease: "easeInOut",
              }}
            />
          ))}

          {/* Gradient orbs */}
          <motion.div
            className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px]"
            animate={{ x: [0, 100, 0], y: [0, -50, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px]"
            animate={{ x: [0, -80, 0], y: [0, 60, 0] }}
            transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Network visualization lines */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 1000 800" preserveAspectRatio="none">
            <motion.path
              d="M0,400 Q250,200 500,400 T1000,400"
              fill="none"
              stroke="#06b6d4"
              strokeWidth="1"
              animate={{ d: ["M0,400 Q250,200 500,400 T1000,400", "M0,400 Q250,600 500,400 T1000,400", "M0,400 Q250,200 500,400 T1000,400"] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.path
              d="M0,500 Q300,300 600,500 T1000,300"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="0.5"
              animate={{ d: ["M0,500 Q300,300 600,500 T1000,300", "M0,500 Q300,700 600,500 T1000,300", "M0,500 Q300,300 600,500 T1000,300"] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>

          <div className="section-container relative z-10 w-full py-20">
            <div className="max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.span
                  className="inline-block px-4 py-1.5 mb-6 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  {heroData.name}
                </motion.span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-balance"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.7 }}
              >
                <span className="gradient-text">{heroData.title}</span>
              </motion.h1>

              <motion.p
                className="text-lg sm:text-xl text-slate-300 font-mono mb-4 tracking-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                {heroData.subtitle}
              </motion.p>

              <motion.p
                className="text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {heroData.description}
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <motion.a
                  href="#projects"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full font-semibold shadow-lg hover:shadow-cyan-500/25 transition-all text-sm"
                >
                  Explore My Work
                </motion.a>
                <motion.a
                  href="#research"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3 border border-slate-600 text-slate-300 rounded-full font-semibold hover:border-cyan-500/50 hover:text-cyan-300 transition-all text-sm"
                >
                  View Research
                </motion.a>
                <motion.a
                  href="https://github.com/wealthymanyasa"
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3 border border-slate-600 text-slate-300 rounded-full font-semibold hover:border-cyan-500/50 hover:text-cyan-300 transition-all text-sm flex items-center gap-2"
                >
                  <ion-icon name="logo-github"></ion-icon>
                  GitHub
                </motion.a>
                <motion.a
                  href="#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-8 py-3 border border-slate-600 text-slate-300 rounded-full font-semibold hover:border-cyan-500/50 hover:text-cyan-300 transition-all text-sm"
                >
                  Contact Me
                </motion.a>
              </motion.div>
            </div>
          </div>
        </section>
    );
};

export default Hero;
